import { FC, useEffect } from 'react';
import DefectsTable from './DefectsTable';
import { useListState } from '@shared/hooks/useListState';
import { getDefectsRequest, getDefectsReset } from '../store';
import ComponentWithChunks from '@shared/components/others/ComponentWithChunk/ComponentWithChunk';
import { useDefectsChunk } from '../hooks/useDefectsChunk';
import ModalErrorContainer from '@shared/components/modals/ModalErrorContainer';
import { useTranslation } from 'react-i18next';
import {
    selectAddDefectStatus,
    selectCloseDefectsStatus,
    selectDeleteDefectsStatus,
    selectDeleteDefectStatus,
    selectGetDefectsError,
    selectGetDefectsStatus,
} from '../store/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { RequestStatusEnum } from '@shared/types';
import { usePrevious } from '@shared/hooks/usePrevious';

interface DefectsListProps {
    testcaseId?: number;
}

const DefectsList = (props: DefectsListProps) => (
    <ComponentWithChunks
        featuresArray={['defects']}
        hooksArray={[useDefectsChunk]}
        Component={DefectsListComponent}
        {...props}
    />
);

const DefectsListComponent: FC<DefectsListProps> = ({ testcaseId }) => {
    const defectsDeleteStatus = useSelector(selectDeleteDefectsStatus);
    const {
        rowSelection,
        pagination,
        setPagination,
        setRowSelection,
        columnFilters,
        setColumnFilters,
        showColumnFilters,
        setShowColumnFilters,
        sorting,
        setSorting,
        fetchData,
    } = useListState('defectsTable', false, getDefectsRequest, {
        deleteStatus: defectsDeleteStatus,
        testcaseId,
        disableUrlParams: Boolean(testcaseId),
    });
    const { t } = useTranslation();
    const getDefectsStatus = useSelector(selectGetDefectsStatus);
    const closeDefectsStatus = useSelector(selectCloseDefectsStatus);
    const addDefectStatus = useSelector(selectAddDefectStatus);
    const deleteDefectStatus = useSelector(selectDeleteDefectStatus);
    const prevCloseDefectsStatus = usePrevious(closeDefectsStatus);
    const prevAddDefectStatus = usePrevious(addDefectStatus);
    const prevDeleteDefectStatus = usePrevious(deleteDefectStatus);
    const dispatch = useDispatch();

    useEffect(() => {
        const statuses = [
            [closeDefectsStatus, prevCloseDefectsStatus, 'close'],
            [addDefectStatus, prevAddDefectStatus, 'add'],
            [deleteDefectStatus, prevDeleteDefectStatus, 'delete'],
        ];

        statuses.forEach(([status, prevStatus, action]) => {
            if (status === RequestStatusEnum.Success && prevStatus !== status) {
                fetchData(action);
                setRowSelection({});
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        addDefectStatus,
        closeDefectsStatus,
        deleteDefectStatus,
        dispatch,
        prevAddDefectStatus,
        prevCloseDefectsStatus,
        prevDeleteDefectStatus,
    ]);

    return (
        <>
            <DefectsTable
                pagination={pagination}
                setPagination={setPagination}
                rowSelection={rowSelection}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                sorting={sorting}
                setSorting={setSorting}
                setRowSelection={setRowSelection}
                showColumnFilters={showColumnFilters}
                setShowColumnFilters={setShowColumnFilters}
                testcaseId={testcaseId}
            />
            {getDefectsStatus === RequestStatusEnum.Error && (
                <ModalErrorContainer
                    object={t('defects_r')}
                    action={t('fetching')}
                    errorSelector={selectGetDefectsError}
                    resetActionCallback={getDefectsReset}
                />
            )}
        </>
    );
};

export default DefectsList;
